import { useEffect, useMemo, useState } from "react";
import { CaretDown, CaretLeft, CaretRight, CaretUp, CaretUpDown } from "@phosphor-icons/react";

const PAGE_SIZE = 10;

const comparableValue = (item, key) => {
  const value = typeof key === "function" ? key(item) : item?.[key];
  if (typeof value === "string") return value.trim();
  return value;
};

const compareValues = (left, right) => {
  if (left == null && right == null) return 0;
  if (left == null || left === "") return 1;
  if (right == null || right === "") return -1;
  if (typeof left === "number" && typeof right === "number") return left - right;
  if (typeof left === "boolean" && typeof right === "boolean") return Number(left) - Number(right);
  return String(left).localeCompare(String(right), "tr", { numeric: true, sensitivity: "base" });
};

export const useSortablePagination = (items, pageSize = PAGE_SIZE, defaultSort = null) => {
  const [sort, setSort] = useState(() => defaultSort || { key: null, direction: "asc" });
  const [page, setPage] = useState(1);

  const sortedItems = useMemo(() => {
    if (!sort.key) return items;
    return items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => {
        const result = compareValues(comparableValue(a.item, sort.key), comparableValue(b.item, sort.key));
        return result === 0 ? a.index - b.index : result * (sort.direction === "asc" ? 1 : -1);
      })
      .map(({ item }) => item);
  }, [items, sort]);

  const pageCount = Math.max(1, Math.ceil(sortedItems.length / pageSize));

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const requestSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
    setPage(1);
  };

  const start = (page - 1) * pageSize;
  return {
    pageItems: sortedItems.slice(start, start + pageSize),
    page,
    pageCount,
    pageSize,
    totalItems: sortedItems.length,
    sort,
    requestSort,
    setPage,
  };
};

export const SortButton = ({ label, sortKey, sort, onSort, align = "left", className = "" }) => {
  const active = sort.key === sortKey;
  const Icon = !active ? CaretUpDown : sort.direction === "asc" ? CaretUp : CaretDown;
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className={`group inline-flex w-full items-center gap-1.5 hover:text-neutral-900 transition-colors ${align === "right" ? "justify-end" : "justify-start"} ${className}`}
    >
      <span>{label}</span>
      <Icon size={13} weight="bold" className={active ? "text-neutral-900" : "text-neutral-400 group-hover:text-neutral-700"} />
    </button>
  );
};

export const SortableHeader = ({ label, sortKey, sort, onSort, className = "", align = "left" }) => {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      aria-sort={!active ? "none" : sort.direction === "asc" ? "ascending" : "descending"}
      className={`px-4 py-3 label-mini ${className}`}
    >
      <SortButton label={label} sortKey={sortKey} sort={sort} onSort={onSort} align={align} />
    </th>
  );
};

const visiblePages = (page, pageCount) => {
  const pages = [];
  for (let value = 1; value <= pageCount; value += 1) {
    if (value === 1 || value === pageCount || Math.abs(value - page) <= 1) pages.push(value);
  }
  return pages.reduce((result, value, index) => {
    if (index > 0 && value - pages[index - 1] > 1) result.push(`gap-${value}`);
    result.push(value);
    return result;
  }, []);
};

export const TablePagination = ({ page, pageCount, pageSize, totalItems, onPageChange }) => {
  if (pageCount <= 1) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, totalItems);
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-[#E5E5E5] px-4 py-3">
      <div className="text-xs text-neutral-500 num">{first}–{last} / {totalItems} kayıt</div>
      <div className="flex items-center gap-1" aria-label="Tablo sayfaları">
        <button type="button" onClick={() => onPageChange(page - 1)} disabled={page === 1} className="p-2 border border-[#E5E5E5] rounded-sm disabled:opacity-35 hover:bg-neutral-100" aria-label="Önceki sayfa">
          <CaretLeft size={15} weight="bold" />
        </button>
        {visiblePages(page, pageCount).map((value) => typeof value === "string" ? (
          <span key={value} className="w-8 text-center text-neutral-400">…</span>
        ) : (
          <button
            type="button"
            key={value}
            onClick={() => onPageChange(value)}
            aria-current={value === page ? "page" : undefined}
            className={`h-8 min-w-8 px-2 rounded-sm text-sm font-semibold ${value === page ? "bg-neutral-900 text-white" : "border border-[#E5E5E5] hover:bg-neutral-100"}`}
          >
            {value}
          </button>
        ))}
        <button type="button" onClick={() => onPageChange(page + 1)} disabled={page === pageCount} className="p-2 border border-[#E5E5E5] rounded-sm disabled:opacity-35 hover:bg-neutral-100" aria-label="Sonraki sayfa">
          <CaretRight size={15} weight="bold" />
        </button>
      </div>
    </div>
  );
};
